'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import { LEGAL_VERSION } from '@/lib/legal/documents';

/**
 * The shell the three public documents share.
 *
 * These pages are read by someone deciding whether to trust the product with
 * their business figures, often before they have an account — so they sit
 * outside the app shell, carry their own way back, and read at a comfortable
 * measure rather than filling a monitor.
 *
 * They follow the app's language the same way every other screen does. A
 * privacy notice in a language you do not read is not a notice.
 */
export default function LegalDoc({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  const { language } = useLanguage();
  const isEs = language === 'es';

  const tabs = [
    { href: '/terms', label: isEs ? 'Términos' : 'Terms' },
    { href: '/privacy', label: isEs ? 'Privacidad' : 'Privacy' },
    { href: '/faq', label: isEs ? 'Preguntas' : 'FAQ' },
  ];

  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-hairline">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 pt-[calc(1rem+env(safe-area-inset-top))] sm:px-8 sm:pt-4">
          <Link href="/dashboard" className="flex items-center gap-2.5">
            <AppLogo className="h-7 w-7" />
            <span className="font-display text-[16px] font-semibold tracking-tight text-ink">
              Quantifyr
            </span>
          </Link>
          <Link
            href="/dashboard"
            className="flex min-h-[44px] items-center gap-1.5 text-[13px] font-medium text-ink-faint transition-colors hover:text-ink-soft"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.1} />
            {isEs ? 'Volver' : 'Back'}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-8 sm:px-8 sm:pt-12">
        <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-ink sm:text-[32px]">
          {title}
        </h1>
        <p className="mt-2 text-[12.5px] text-ink-ghost">
          {isEs ? 'Última actualización' : 'Last updated'} {LEGAL_VERSION}
        </p>
        {intro && (
          <p className="mt-5 text-[15px] leading-relaxed text-ink-soft sm:text-[16px]">{intro}</p>
        )}

        {/* Between the three, because the question that sends you to one of
            them is usually answered by another. */}
        <nav className="mt-6 flex flex-wrap gap-2 border-y border-hairline py-3">
          {tabs.map((tab) => (
            <Link
              key={tab.href}
              href={tab.href}
              className="rounded-lg px-3 py-2 text-[13px] font-medium text-ink-faint transition-colors hover:bg-surface hover:text-ink"
            >
              {tab.label}
            </Link>
          ))}
        </nav>

        <div className="mt-8">{children}</div>
      </main>
    </div>
  );
}
